import { useEffect, useMemo, useState } from "react";
import { MagnifyingGlass, Plus, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { prefetchWizardCatalog, getCachedWizardCatalog } from "../lib/catalogCache";

export const STORAGE_OPTIONS = ["32GB", "64GB", "128GB", "256GB", "512GB", "1TB", "2TB"];
export const CONDITION_OPTIONS = [
  { v: "Excellent", sub: "Like new" },
  { v: "Good", sub: "Light wear" },
  { v: "Fair", sub: "Visible marks" },
  { v: "Poor", sub: "Heavy wear" },
  { v: "Faulty", sub: "Needs repair" },
];

// Brands → models from the repair catalog plus shop-added trade-in models.
function useTradeInCatalog() {
  const [website, setWebsite] = useState(() => getCachedWizardCatalog().websiteCatalog);
  const [custom, setCustom] = useState([]);
  useEffect(() => {
    prefetchWizardCatalog().then((c) => setWebsite(c.websiteCatalog)).catch(() => {});
    api.get("/trade-in-models").then((r) => setCustom(r.data || [])).catch(() => {});
  }, []);
  const brands = useMemo(() => {
    const map = new Map();
    const add = (make, model, type) => {
      if (!make) return;
      const key = make.toLowerCase();
      if (!map.has(key)) map.set(key, { name: make, models: [] });
      if (model && !map.get(key).models.some((m) => m.name.toLowerCase() === model.toLowerCase())) {
        map.get(key).models.push({ name: model, type: type || "" });
      }
    };
    (website?.manufacturers || []).forEach((m) =>
      (m.device_types || []).forEach((dt) => (dt.models || []).forEach((md) => add(m.name, md.name, dt.name))));
    (website?.manufacturers || []).forEach((m) => add(m.name));
    custom.forEach((c) => add(c.make, c.model, c.device_type));
    return [...map.values()];
  }, [website, custom]);
  return { brands, addCustom: (doc) => setCustom((list) => [...list, doc]) };
}

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`h-9 px-3 rounded-full text-[12px] font-semibold border transition-colors ${
      active ? "bg-[#1E5FEB] border-[#1E5FEB] text-white" : "bg-white border-[#E5E7EB] text-[#374151] hover:border-[#1E5FEB]/50"
    }`}
  >
    {children}
  </button>
);

const Label = ({ children }) => (
  <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#6B7280] mb-1.5">{children}</div>
);

function ModelSearch({ brand, value, models, onPick, onAdd, testid }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  useEffect(() => { setQ(value || ""); }, [value]);
  const needle = q.trim().toLowerCase();
  const hits = models.filter((m) => !needle || m.name.toLowerCase().includes(needle)).slice(0, 8);
  const exact = models.some((m) => m.name.toLowerCase() === needle);
  return (
    <div className="relative">
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); onPick(e.target.value, ""); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={brand ? `Search ${brand} models…` : "Pick a brand first, or type the model"}
          className="w-full h-11 pl-9 pr-3 rounded-lg border border-[#E5E7EB] text-[14px] focus:border-[#1E5FEB] outline-none"
          data-testid={testid}
        />
      </div>
      {open && (hits.length > 0 || (needle && !exact)) && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {hits.map((m) => (
            <button
              key={m.name}
              type="button"
              onMouseDown={() => { onPick(m.name, m.type); setQ(m.name); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-[13px] hover:bg-[#EFF6FF] flex items-center justify-between"
              data-testid={`${testid}-option-${m.name}`}
            >
              <span>{m.name}</span>
              {m.type && <span className="text-[10.5px] text-[#9CA3AF]">{m.type}</span>}
            </button>
          ))}
          {needle && !exact && (
            <button
              type="button"
              onMouseDown={() => { onAdd(q.trim()); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 text-[13px] font-semibold text-[#1E5FEB] hover:bg-[#EFF6FF] border-t border-[#F3F4F6] flex items-center gap-1.5"
              data-testid={`${testid}-add-new`}
            >
              <Plus size={13} weight="bold" /> Add "{q.trim()}"{brand ? ` to ${brand}` : ""} as a new model
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Make / model / storage / condition picker shared by trade-in intake and edit.
export function TradeInDeviceFields({ value, onChange, idPrefix = "ti" }) {
  const { brands, addCustom } = useTradeInCatalog();
  const brand = brands.find((b) => b.name.toLowerCase() === (value.device_make || "").toLowerCase());
  const topBrands = brands.slice(0, 8);
  const [otherBrand, setOtherBrand] = useState(false);
  const [customStorage, setCustomStorage] = useState(!!value.storage && !STORAGE_OPTIONS.includes(value.storage));

  const addModel = async (model) => {
    if (!value.device_make?.trim()) { toast.error("Pick or type the brand first"); return; }
    try {
      const { data } = await api.post("/trade-in-models", { make: value.device_make.trim(), model, device_type: value.device_type || "" });
      addCustom(data);
      onChange({ device_model: data.model });
      toast.success(`${data.make} ${data.model} added to the list`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label>Brand *</Label>
        <div className="flex flex-wrap gap-1.5">
          {topBrands.map((b) => (
            <Chip key={b.name} active={brand?.name === b.name && !otherBrand}
                  onClick={() => { setOtherBrand(false); onChange({ device_make: b.name, device_model: "", device_type: "" }); }}
                  testid={`${idPrefix}-brand-${b.name}`}>{b.name}</Chip>
          ))}
          <Chip active={otherBrand || (!!value.device_make && !topBrands.some((b) => b.name === brand?.name))}
                onClick={() => { setOtherBrand(true); onChange({ device_make: "", device_model: "" }); }}
                testid={`${idPrefix}-brand-other`}>Other…</Chip>
        </div>
        {(otherBrand || (!!value.device_make && !topBrands.some((b) => b.name === brand?.name))) && (
          <input
            value={value.device_make}
            onChange={(e) => onChange({ device_make: e.target.value })}
            placeholder="Type the brand, e.g. Nokia"
            list={`${idPrefix}-brand-list`}
            className="mt-2 w-full h-10 px-3 rounded-lg border border-[#E5E7EB] text-[13px] focus:border-[#1E5FEB] outline-none"
            data-testid={`${idPrefix}-brand-input`}
          />
        )}
        <datalist id={`${idPrefix}-brand-list`}>{brands.map((b) => <option key={b.name} value={b.name} />)}</datalist>
      </div>

      <div>
        <Label>Model *</Label>
        <ModelSearch
          brand={brand?.name || value.device_make}
          value={value.device_model}
          models={brand?.models || []}
          onPick={(name, type) => onChange({ device_model: name, ...(type ? { device_type: type } : {}) })}
          onAdd={addModel}
          testid={`${idPrefix}-model`}
        />
      </div>

      <div>
        <Label>Storage</Label>
        <div className="flex flex-wrap gap-1.5">
          {STORAGE_OPTIONS.map((s) => (
            <Chip key={s} active={value.storage === s} testid={`${idPrefix}-storage-${s}`}
                  onClick={() => { setCustomStorage(false); onChange({ storage: value.storage === s ? "" : s }); }}>{s}</Chip>
          ))}
          <Chip active={customStorage} testid={`${idPrefix}-storage-other`}
                onClick={() => { setCustomStorage(true); onChange({ storage: "" }); }}>Other</Chip>
        </div>
        {customStorage && (
          <input
            value={value.storage}
            onChange={(e) => onChange({ storage: e.target.value })}
            placeholder="e.g. 16GB / 8GB RAM + 256GB"
            className="mt-2 w-full h-10 px-3 rounded-lg border border-[#E5E7EB] text-[13px] focus:border-[#1E5FEB] outline-none"
            data-testid={`${idPrefix}-storage-input`}
          />
        )}
      </div>

      <div>
        <Label>Condition</Label>
        <div className="grid grid-cols-5 gap-1.5">
          {CONDITION_OPTIONS.map((c) => {
            const active = value.condition === c.v;
            return (
              <button
                key={c.v}
                type="button"
                onClick={() => onChange({ condition: c.v })}
                data-testid={`${idPrefix}-condition-${c.v}`}
                className={`p-2 rounded-lg border-2 text-left transition-colors ${active ? "border-[#1E5FEB] bg-[#EFF6FF]" : "border-[#E5E7EB] bg-white hover:border-[#1E5FEB]/40"}`}
              >
                <div className="text-[12px] font-semibold flex items-center gap-1">{active && <Check size={11} weight="bold" className="text-[#1E5FEB]" />}{c.v}</div>
                <div className="text-[10px] text-[#6B7280]">{c.sub}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
